import { useState, useMemo } from "react";
import {
    Dialog,
    DialogContent,
    DialogActions,
    Box,
    Button,
    Typography,
    TextField,
    IconButton,
    InputAdornment,
    Paper,
    Chip
} from "@mui/material";
import {
    Close as CloseIcon,
    Search as SearchIcon,
    FilterAltOutlined as FilterIcon,
    Check as CheckIcon,
    Close as XIcon,
    Visibility as ViewIcon
} from "@mui/icons-material";
import { formatDateHebrew } from "../../utils/formUtils";
import FormViewDialog from "../client/FormViewDialog";

const FormRecipientsDialog = ({ open, onClose, form }) => {
    const [filter, setFilter] = useState("all"); // 'all' | 'answered' | 'not-answered'
    const [searchQuery, setSearchQuery] = useState("");
    const [viewFormOpen, setViewFormOpen] = useState(false);
    const [selectedRecipient, setSelectedRecipient] = useState(null);

    const handleViewForm = (recipient) => {
        setSelectedRecipient(recipient);
        setViewFormOpen(true);
    };

    const recipients = form?.recipients || [];

    // Filter recipients
    const filteredRecipients = useMemo(() => {
        return recipients.filter(recipient => {
            // Filter by status
            if (filter === "answered" && !recipient.answered) return false;
            if (filter === "not-answered" && recipient.answered) return false;

            // Filter by search query
            if (searchQuery.trim()) {
                return recipient.name.toLowerCase().includes(searchQuery.toLowerCase());
            }

            return true;
        });
    }, [recipients, filter, searchQuery]);

    // Stats
    const answeredCount = recipients.filter(r => r.answered).length;
    const notAnsweredCount = recipients.filter(r => !r.answered).length;

    if (!form) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    maxHeight: "90vh",
                    direction: "rtl"
                }
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    padding: "20px 24px",
                    borderBottom: "1px solid #E2E8F0"
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: "20px",
                            color: "#1E293B",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            marginBottom: "4px"
                        }}
                    >
                        נשלח ל...
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif'
                        }}
                    >
                        {form.title}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: "#64748B" }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Stats */}
            <Box
                sx={{
                    padding: "20px 24px",
                    borderBottom: "1px solid #E2E8F0",
                    backgroundColor: "#F8FAFC"
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "16px"
                    }}
                >
                    <Box sx={{ textAlign: "center" }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "28px",
                                color: "#1E293B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {recipients.length}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            סה"כ נשלח
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "28px",
                                color: "#16A34A",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {answeredCount}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            ענו
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: "28px",
                                color: "#EA580C",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            {notAnsweredCount}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                color: "#64748B",
                                fontFamily: '"Heebo", "Roboto", sans-serif'
                            }}
                        >
                            לא ענו
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Filters */}
            <Box
                sx={{
                    padding: "16px 24px",
                    borderBottom: "1px solid #E2E8F0"
                }}
            >
                {/* Search */}
                <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="חיפוש לפי שם..."
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "#64748B", fontSize: 20 }} />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        marginBottom: "16px",
                        "& .MuiOutlinedInput-root": {
                            borderRadius: "8px"
                        }
                    }}
                />

                {/* Status Filters */}
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <FilterIcon sx={{ color: "#57837E", fontSize: 22 }} />
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#64748B",
                            fontFamily: '"Heebo", "Roboto", sans-serif',
                            marginLeft: "8px"
                        }}
                    >
                        סינון:
                    </Typography>
                    <Box sx={{ display: "flex", gap: "8px" }}>
                        <Chip
                            label={`הכל (${recipients.length})`}
                            onClick={() => setFilter("all")}
                            sx={{
                                backgroundColor: filter === "all" ? "#57837E" : "#F1F5F9",
                                color: filter === "all" ? "#FFFFFF" : "#64748B",
                                fontWeight: 600,
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                "&:hover": {
                                    backgroundColor: filter === "all" ? "#46706B" : "#E2E8F0"
                                }
                            }}
                        />
                        <Chip
                            label={`ענה (${answeredCount})`}
                            onClick={() => setFilter("answered")}
                            sx={{
                                backgroundColor: filter === "answered" ? "#57837E" : "#F1F5F9",
                                color: filter === "answered" ? "#FFFFFF" : "#64748B",
                                fontWeight: 600,
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                "&:hover": {
                                    backgroundColor: filter === "answered" ? "#46706B" : "#E2E8F0"
                                }
                            }}
                        />
                        <Chip
                            label={`לא ענה (${notAnsweredCount})`}
                            onClick={() => setFilter("not-answered")}
                            sx={{
                                backgroundColor: filter === "not-answered" ? "#57837E" : "#F1F5F9",
                                color: filter === "not-answered" ? "#FFFFFF" : "#64748B",
                                fontWeight: 600,
                                fontFamily: '"Heebo", "Roboto", sans-serif',
                                "&:hover": {
                                    backgroundColor: filter === "not-answered" ? "#46706B" : "#E2E8F0"
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Recipients List */}
            <DialogContent sx={{ padding: "24px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {filteredRecipients.map((recipient) => (
                        <Paper
                            key={recipient.id}
                            elevation={0}
                            onClick={recipient.answered && recipient.formId ? () => handleViewForm(recipient) : undefined}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "16px",
                                border: recipient.answered
                                    ? "1px solid #BBF7D0"
                                    : "1px solid #FED7AA",
                                backgroundColor: recipient.answered
                                    ? "#F0FDF4"
                                    : "#FFF7ED",
                                borderRadius: "8px",
                                ...(recipient.answered && recipient.formId ? {
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                        borderColor: "#57837E"
                                    }
                                } : {})
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: recipient.answered
                                            ? "#DCFCE7"
                                            : "#FFEDD5"
                                    }}
                                >
                                    {recipient.answered ? (
                                        <CheckIcon sx={{ color: "#16A34A", fontSize: 20 }} />
                                    ) : (
                                        <XIcon sx={{ color: "#EA580C", fontSize: 20 }} />
                                    )}
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: "14px",
                                            color: "#1E293B",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}
                                    >
                                        {recipient.name}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: recipient.answered ? "#15803D" : "#C2410C",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}
                                    >
                                        {recipient.answered
                                            ? `ענה בתאריך ${formatDateHebrew(recipient.answeredDate)}`
                                            : "עדיין לא ענה"
                                        }
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                {recipient.answered && recipient.formId && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewForm(recipient);
                                        }}
                                        sx={{
                                            color: "#57837E",
                                            "&:hover": {
                                                backgroundColor: "rgba(87, 131, 126, 0.1)"
                                            }
                                        }}
                                    >
                                        <ViewIcon sx={{ fontSize: 20 }} />
                                    </IconButton>
                                )}
                                {recipient.answered && (
                                    <Chip
                                        label="הושלם"
                                        size="small"
                                        sx={{
                                            backgroundColor: "#16A34A",
                                            color: "#FFFFFF",
                                            fontWeight: 600,
                                            fontSize: "12px",
                                            fontFamily: '"Heebo", "Roboto", sans-serif'
                                        }}
                                    />
                                )}
                            </Box>
                        </Paper>
                    ))}

                    {filteredRecipients.length === 0 && (
                        <Box sx={{ textAlign: "center", padding: "48px 24px" }}>
                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    color: "#64748B",
                                    fontFamily: '"Heebo", "Roboto", sans-serif'
                                }}
                            >
                                {recipients.length === 0
                                    ? "הטופס עדיין לא נשלח לאף מתאמן"
                                    : "אין תוצאות בסינון זה"
                                }
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* Footer */}
            <DialogActions
                sx={{
                    padding: "16px 24px",
                    borderTop: "1px solid #E2E8F0",
                    justifyContent: "flex-end"
                }}
            >
                <Button
                    onClick={onClose}
                    sx={{
                        backgroundColor: "#57837E",
                        color: "#FFFFFF",
                        fontFamily: '"Heebo", "Roboto", sans-serif',
                        fontWeight: 600,
                        borderRadius: "8px",
                        padding: "8px 24px",
                        "&:hover": {
                            backgroundColor: "#46706B"
                        }
                    }}
                >
                    סגור
                </Button>
            </DialogActions>

            {/* Form View Dialog */}
            <FormViewDialog
                open={viewFormOpen}
                onClose={() => {
                    setViewFormOpen(false);
                    setSelectedRecipient(null);
                }}
                form={selectedRecipient ? { id: selectedRecipient.formId } : null}
                clientName={selectedRecipient?.name}
            />
        </Dialog>
    );
};

export default FormRecipientsDialog;
